import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Grid, IconButton, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
  Button,
} from '@mui/material';
import {
  Assignment as AssignmentIcon, Business as BusinessIcon, Close as CloseIcon,
  Grade as GradeIcon, People as PeopleIcon, ReportProblemOutlined as RuptureIcon,
} from '@mui/icons-material';
import ProcurementService from '../../../services/ProcurementService';
import { formatDate, formatPartnerSource } from '../../../helpers/PartnerHelper';
import { partnerTableColors, partnerTableSx } from '../Parterns/partnerListTheme';

const valueOrDash = (value) => value || '—';

const Info = ({ label, value }) => (
  <Box>
    <Typography sx={{ color: '#667085', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{label}</Typography>
    <Typography sx={{ color: '#101828', fontSize: 14, mt: 0.3, overflowWrap: 'anywhere' }}>{valueOrDash(value)}</Typography>
  </Box>
);

const SectionTitle = ({ icon, children }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5, color: partnerTableColors.dark }}>
    {icon}<Typography sx={{ fontWeight: 800, fontSize: 16 }}>{children}</Typography>
  </Stack>
);

const MarcheDetailsModal = ({ open, market, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    if (!open || !market) return undefined;
    setLoading(true); setError(''); setDetail(null);
    ProcurementService.getProcurementDetails(market).then((response) => {
      if (!active) return;
      if (response?.status && response?.data) setDetail(response.data);
      else setError(response?.message || 'Impossible de charger le détail du marché.');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, market]);

  const titulaire = detail?.titulaire || {};
  const personnel = Array.isArray(detail?.personnel) ? detail.personnel : [];
  const evaluations = Array.isArray(detail?.evaluations) ? detail.evaluations : [];
  const isCompleted = String(detail?.situation || '').toUpperCase() === 'FIN';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
      <DialogTitle sx={{ bgcolor: partnerTableColors.dark, color: '#fff', p: 2.2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box>
            <Typography sx={{ fontSize: 11, opacity: 0.75, fontWeight: 700 }}>DÉTAIL DU MARCHÉ</Typography>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{detail?.numero || market?.numero || 'Marché'}</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              <Chip size="small" label={detail?.situation || market?.situation || ''} sx={{ bgcolor: '#fff', color: partnerTableColors.dark, fontWeight: 700 }} />
              <Chip size="small" label={formatPartnerSource(detail?.source_db || market?.source_db)} sx={{ bgcolor: partnerTableColors.gold, color: '#fff', fontWeight: 700 }} />
              {detail?.read_only && <Chip size="small" label="Lecture seule" variant="outlined" sx={{ color: '#fff', borderColor: '#fff' }} />}
            </Stack>
          </Box>
          <Tooltip title="Fermer"><IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton></Tooltip>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: partnerTableColors.soft, p: { xs: 1.5, md: 2.5 } }}>
        {loading && <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 280 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}
        {!loading && detail && <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2, borderColor: partnerTableColors.border, borderRadius: 2 }}>
            <SectionTitle icon={<BusinessIcon />}>Titulaire du marché</SectionTitle>
            <Typography sx={{ fontSize: 19, fontWeight: 800, color: partnerTableColors.dark, mb: 2 }}>{valueOrDash(titulaire.nom)}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}><Info label="Statut juridique" value={titulaire.statut_juridique} /></Grid>
              <Grid item xs={6} sm={4}><Info label="NIF" value={titulaire.nif} /></Grid>
              <Grid item xs={6} sm={4}><Info label="STAT" value={titulaire.stat} /></Grid>
              <Grid item xs={12} sm={4}><Info label="Contact" value={titulaire.contact} /></Grid>
              <Grid item xs={12} sm={4}><Info label="Téléphone" value={titulaire.telephone} /></Grid>
              <Grid item xs={12} sm={4}><Info label="Email" value={titulaire.email} /></Grid>
            </Grid>
          </Paper>

          {(detail.date_rupture || detail.cause_rupture) && <Paper variant="outlined" sx={{ p: 2, borderColor: isCompleted ? partnerTableColors.border : '#f4b4b0', borderRadius: 2, bgcolor: isCompleted ? '#f8fbff' : '#fff8f7' }}>
            <SectionTitle icon={<RuptureIcon />}>{isCompleted ? 'Fin du contrat' : 'Résiliation'}</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}><Info label={isCompleted ? 'Date de fin' : 'Date de rupture'} value={formatDate(detail.date_rupture)} /></Grid>
              <Grid item xs={12} sm={8}><Info label={isCompleted ? 'Motif de fin' : 'Cause de rupture'} value={detail.cause_rupture} /></Grid>
            </Grid>
          </Paper>}

          <Paper variant="outlined" sx={{ p: 2, borderColor: partnerTableColors.border, borderRadius: 2 }}>
            <SectionTitle icon={<AssignmentIcon />}>Informations du contrat</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12}><Info label="Objet" value={detail.objet} /></Grid>
              <Grid item xs={6} md={3}><Info label="Référence contrat" value={detail.ref_num_contrat} /></Grid>
              <Grid item xs={6} md={3}><Info label="Direction" value={detail.direction} /></Grid>
              <Grid item xs={6} md={3}><Info label="Financement" value={detail.financement} /></Grid>
              <Grid item xs={6} md={3}><Info label="Composante" value={detail.composante} /></Grid>
              <Grid item xs={6} md={3}><Info label="Zone" value={detail.zone} /></Grid>
              <Grid item xs={6} md={3}><Info label="Signature" value={formatDate(detail.date_signature_contrat)} /></Grid>
              <Grid item xs={6} md={3}><Info label="Début" value={formatDate(detail.debut)} /></Grid>
              <Grid item xs={6} md={3}><Info label="Fin" value={formatDate(detail.fin)} /></Grid>
              {/* <Grid item xs={6} md={3}><Info label="Notification" value={formatDate(detail.date_notification)} /></Grid>
              <Grid item xs={6} md={3}><Info label="Ordre de service" value={formatDate(detail.date_os)} /></Grid> */}
            </Grid>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderColor: partnerTableColors.border, borderRadius: 2 }}>
            <SectionTitle icon={<PeopleIcon />}>Personnel affecté à ce contrat</SectionTitle>
            {!personnel.length ? <Typography color="text.secondary">Aucun personnel affecté à ce contrat.</Typography> : (
              <TableContainer><Table size="small" sx={partnerTableSx}><TableHead><TableRow>
                <TableCell>Nom</TableCell><TableCell>Fonction</TableCell><TableCell>CIN</TableCell><TableCell>Contact</TableCell>
              </TableRow></TableHead><TableBody>{personnel.map((person, index) => <TableRow key={`${person.pe || person.nom}-${index}`}>
                <TableCell>{valueOrDash(person.nom)}</TableCell><TableCell>{valueOrDash(person.fonction)}</TableCell>
                <TableCell>{valueOrDash(person.cin)}</TableCell><TableCell>{person.telephone || person.email || '—'}</TableCell>
              </TableRow>)}</TableBody></Table></TableContainer>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderColor: partnerTableColors.border, borderRadius: 2 }}>
            <SectionTitle icon={<GradeIcon />}>Historique des évaluations de ce contrat</SectionTitle>
            {!evaluations.length ? <Typography color="text.secondary">Aucune évaluation enregistrée pour ce contrat.</Typography> : (
              <Stack divider={<Divider flexItem />} spacing={1}>{evaluations.map((evaluation, index) => <Stack key={`${evaluation.date_evaluation}-${index}`} direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ py: 0.8 }}>
                <Typography sx={{ fontWeight: 700 }}>{formatDate(evaluation.date_evaluation)}</Typography>
                <Chip size="small" label={valueOrDash(evaluation.resultat)} />
              </Stack>)}</Stack>
            )}
          </Paper>
        </Stack>}
      </DialogContent>
      <DialogActions sx={{ borderTop: `1px solid ${partnerTableColors.border}`, px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} variant="contained" sx={{ bgcolor: partnerTableColors.dark, '&:hover': { bgcolor: '#102e49' } }}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MarcheDetailsModal;
